import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { useContext, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useField, useFormikContext, ErrorMessage } from 'formik';
import { Flex, Box, Feedback, Text } from '@hausgold/designsystem';
import { ApiContext, WizardContext } from '../../../utils/contexts.js';
import analyticsTrack from '../../../utils/tracking.js';
import Input from '../../../components/Input.js';
import { getNextStepKey, getNextStepPosition } from '../../../services/models/dialog.js';
import { jsx } from '@emotion/react';
var _excluded = ["mandatory", "placeholderStreet", "placeholderZip", "placeholderCity", "stepKey", "lineKey"];
var Address = function Address(_ref) {
  var mandatory = _ref.mandatory,
    placeholderStreet = _ref.placeholderStreet,
    placeholderZip = _ref.placeholderZip,
    placeholderCity = _ref.placeholderCity,
    stepKey = _ref.stepKey,
    lineKey = _ref.lineKey,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useField = useField("".concat(stepKey, "_street")),
    _useField2 = _slicedToArray(_useField, 3),
    fieldStreet = _useField2[0],
    metaStreet = _useField2[1],
    helpersStreet = _useField2[2];
  var _useField3 = useField("".concat(stepKey, "_zip")),
    _useField4 = _slicedToArray(_useField3, 3),
    fieldZip = _useField4[0],
    metaZip = _useField4[1],
    helpersZip = _useField4[2];
  var _useField5 = useField("".concat(stepKey, "_city")),
    _useField6 = _slicedToArray(_useField5, 3),
    fieldCity = _useField6[0],
    metaCity = _useField6[1],
    helpersCity = _useField6[2];
  var formikContext = useFormikContext();
  var _useContext = useContext(ApiContext),
    data = _useContext.data;
  var _useContext2 = useContext(WizardContext),
    currentStep = _useContext2.currentStep,
    setCurrentStep = _useContext2.setCurrentStep,
    currentLine = _useContext2.currentLine,
    dialogId = _useContext2.dialogId,
    recruiterId = _useContext2.recruiterId,
    leadId = _useContext2.leadId;
  var handleEnter = function handleEnter(event) {
    if (event.key === 'Enter') {
      var _metaStreet$value, _metaZip$value, _metaCity$value;
      helpersStreet.setTouched(true);
      helpersZip.setTouched(true);
      helpersCity.setTouched(true);
      formikContext.validateForm();
      event.preventDefault();

      // TODO: Apparently, even with the call to validateForm, formik's
      //   internal state (meta) hasn't gotten the info yet that a field
      //   has an error at this point, so we manually at least check for
      //   non-empty. Optimally, this should work with formik's
      //   validation.
      // (Copy from other inputs)
      if ((_metaStreet$value = metaStreet.value) !== null && _metaStreet$value !== void 0 && _metaStreet$value.length && (_metaZip$value = metaZip.value) !== null && _metaZip$value !== void 0 && _metaZip$value.length && (_metaCity$value = metaCity.value) !== null && _metaCity$value !== void 0 && _metaCity$value.length) {
        var nextStepKey = getNextStepKey(data, currentLine, currentStep);

        // If there is no next step key, submit the form.
        if (!nextStepKey) {
          // Track user progress (last Step; Next is "Kontaktformular").
          analyticsTrack({
            event: 'Formwizard Interacted',
            params: {
              dialog_id: dialogId,
              lead_id: leadId,
              recruiter_id: recruiterId,
              label: 'Enter',
              step: "".concat(currentLine, "-clickto_step_Kontaktformular")
            }
          });
          formikContext.submitForm();
          // Else go to next step.
        } else {
          var nextStepPosition = getNextStepPosition(data, lineKey, stepKey);
          // Track user progress
          analyticsTrack({
            event: 'Formwizard Interacted',
            params: {
              dialog_id: dialogId,
              lead_id: leadId,
              recruiter_id: recruiterId,
              label: event.target.value,
              step: "".concat(lineKey, "-clickto_step").concat(nextStepPosition, "_").concat(nextStepKey)
            }
          });
          setCurrentStep(nextStepKey);
        }
      }
    }
  };
  return jsx(Fragment, null, jsx(Flex, {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
    gridRowGap: 4
    // same maxWidth as in KIT
    ,

    maxWidth: "764px",
    mx: "auto",
    onKeyDown: handleEnter
  }, jsx(Box, {
    width: {
      base: '100%',
      sm: '498px'
    },
    textAlign: "center",
    mx: 4
  }, jsx(Input
  // Street
  , _extends({}, fieldStreet, {
    type: "text",
    inputMode: "text",
    isInvalid: Boolean(metaStreet.touched && metaStreet.error),
    size: "lg",
    placeholder: "".concat(placeholderStreet || '', " ").concat(mandatory ? '*' : ''),
    onKeyDown: function onKeyDown(event) {
      // Only allow special (some) characters
      if (!/[\wäÄöÖüÜß \-0-9]/.test(event.key) && event.key !== 'Backspace' && event.key !== 'Delete' &&
      // Allow hotkeys (CTRL+C, CTRL+V)
      !event.ctrlKey && !event.altKey &&
      // Allow mac's hotkeys (CMD+C, CMD+V)
      !event.metaKey &&
      // Allow left and right arrow keys
      event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        event.preventDefault();
      }
    }
  }, props)), jsx(Flex, {
    mt: 2,
    justifyContent: "space-between"
  }, jsx(ErrorMessage, {
    name: "".concat(stepKey, "_street")
  }, function (message) {
    return jsx(Feedback, {
      mb: 0,
      mx: "auto",
      display: "block"
    }, message);
  }), mandatory && !(metaStreet.touched && metaStreet.error) &&
  // Keep height space to minimize layout shift on error message
  jsx(Text, {
    as: "span",
    ml: "auto",
    color: "gray.400",
    mb: 0
  }, "\xA0"))), jsx(Box, {
    mx: 4,
    width: {
      base: '100%',
      sm: 'inherit'
    }
  }, jsx(Input
  // Zip Code
  , _extends({}, fieldZip, {
    type: "text",
    inputMode: "numeric",
    isInvalid: Boolean(metaZip.touched && metaZip.error),
    width: "100%",
    size: "lg",
    placeholder: "".concat(placeholderZip || '', " ").concat(mandatory ? '*' : ''),
    onKeyDown: function onKeyDown(event) {
      // Only allow special (some) characters
      if (!/[0-9]/.test(event.key) && event.key !== 'Backspace' && event.key !== 'Delete' &&
      // Allow hotkeys (CTRL+C, CTRL+V)
      !event.ctrlKey && !event.altKey &&
      // Allow mac's hotkeys (CMD+C, CMD+V)
      !event.metaKey &&
      // Allow left and right arrow keys
      event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        event.preventDefault();
      }
    }
  }, props)), jsx(Flex, {
    mt: 2,
    justifyContent: "space-between"
  }, jsx(ErrorMessage, {
    name: "".concat(stepKey, "_zip")
  }, function (message) {
    return jsx(Feedback, {
      mb: 0,
      mx: "auto",
      display: "block"
    }, message);
  }), mandatory && !(metaZip.touched && metaZip.error) &&
  // Keep height space to city's mandatory field
  jsx(Text, {
    as: "span",
    ml: "auto",
    color: "gray.400",
    mb: 0
  }, "\xA0"))), jsx(Box, {
    mx: 4,
    width: {
      base: '100%',
      sm: 'inherit'
    }
  }, jsx(Input
  // City
  , _extends({}, fieldCity, {
    type: "text",
    inputMode: "text",
    isInvalid: Boolean(metaCity.touched && metaCity.error),
    width: "100%",
    size: "lg",
    placeholder: "".concat(placeholderCity || '', " ").concat(mandatory ? '*' : ''),
    onKeyDown: function onKeyDown(event) {
      // Only allow special (some) characters
      if (!/[\wäÄöÖüÜß-]/.test(event.key) && event.key !== 'Backspace' && event.key !== 'Delete' &&
      // Allow hotkeys (CTRL+C, CTRL+V)
      !event.ctrlKey && !event.altKey &&
      // Allow mac's hotkeys (CMD+C, CMD+V)
      !event.metaKey &&
      // Allow left and right arrow keys
      event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        event.preventDefault();
      }
    }
  }, props)), jsx(Flex, {
    mt: 2,
    justifyContent: "space-between"
  }, jsx(ErrorMessage, {
    name: "".concat(stepKey, "_city")
  }, function (message) {
    return jsx(Feedback, {
      mb: 0,
      mx: "auto",
      display: "block"
    }, message);
  }), mandatory && !(metaCity.touched && metaCity.error) && jsx(Text, {
    as: "span",
    ml: "auto",
    color: "gray.400",
    mb: 0
  }, "* Pflichtfeld")))));
};
Address.propTypes = {
  mandatory: PropTypes.bool,
  placeholderStreet: PropTypes.string,
  placeholderZip: PropTypes.string,
  placeholderCity: PropTypes.string,
  stepKey: PropTypes.string.isRequired,
  lineKey: PropTypes.string.isRequired
};
Address.defaultProps = {
  mandatory: false,
  placeholderStreet: null,
  placeholderZip: null,
  placeholderCity: null
};
export { Address as default };